import { flattenTree, type TreeNavNode, type TreeSource } from '@krp/ui-kit'

import type { ReceiptRegisters, ReceiptShifts, ReceiptsFilter } from './types'

/**
 * Дерево «Где смотрим» журнала AQSI: кабинет → магазин → касса → смена. Узел — это отбор журнала, а не экран: выбор
 * узла превращается в `nodePatch` и применяется сразу. Идентификаторы узлов — строки с префиксом, чтобы по одному
 * ключу восстановить отбор (и выделение из отбора).
 */
export const ROOT_NODE = 'all'
/** Подпись магазина, которую служба ставит кассе без магазина: отбирать по ней нечем, это папка, а не отбор. */
export const NO_SHOP = 'Без магазина'

/** Сколько недавних смен кассы показывает дерево: остальные — по номеру смены в поиске или «Моим сменам». */
export const MAX_SHIFT_NODES = 20

export const shopNode = (shop: string) => `shop:${shop}`
export const registerNode = (registerId: string) => `reg:${registerId}`
export const shiftNode = (registerId: string, number: number | string) => `shift:${registerId}:${number}`

/** Это узел-папка без собственного отбора («Без магазина»): щелчок по нему только раскрывает. */
export const isFolderNode = (id: string): boolean => id === shopNode(NO_SHOP)

/** Отбор узла: какие поля фильтра он задаёт; остальные поля узла обнуляются (выбор узла — один срез). */
export function nodePatch(id: string): Partial<ReceiptsFilter> | null {
  if (id === ROOT_NODE) return { shop: '', registerId: '', shiftNumber: '' }
  if (id.startsWith('shop:')) return { shop: id.slice('shop:'.length), registerId: '', shiftNumber: '' }
  if (id.startsWith('reg:')) return { shop: '', registerId: id.slice('reg:'.length), shiftNumber: '' }
  if (id.startsWith('shift:')) {
    const rest = id.slice('shift:'.length)
    const cut = rest.lastIndexOf(':')
    if (cut <= 0) return null
    return { shop: '', registerId: rest.slice(0, cut), shiftNumber: rest.slice(cut + 1) }
  }
  return null
}

/** Какой узел выбран отбором: самый узкий из заданных. */
export function selectedNodeOf(filter: Pick<ReceiptsFilter, 'shop' | 'registerId' | 'shiftNumber'>): string {
  if (filter.registerId !== '' && filter.shiftNumber !== '') return shiftNode(filter.registerId, filter.shiftNumber)
  if (filter.registerId !== '') return registerNode(filter.registerId)
  if (filter.shop !== '') return shopNode(filter.shop)
  return ROOT_NODE
}

const plural = (count: number, one: string, few: string, many: string): string => {
  const rest = count % 100
  if (rest >= 11 && rest <= 14) return many
  const last = count % 10
  return last === 1 ? one : last >= 2 && last <= 4 ? few : many
}

const registersText = (count: number) => `${count} ${plural(count, 'касса', 'кассы', 'касс')}`

/** Источник дерева: кассы кабинета, а у выбранной кассы — её последние смены. */
/** Число чеков узла словами для дерева: «4», «0»; чисел нет (итоги не прочитаны) — без числа. */
const countText = (count: number | null): string | null => (count === null ? null : String(count))

export function treeSourceOf(
  registers: ReceiptRegisters | undefined,
  shifts: ReceiptShifts | undefined,
  selectedRegisterId: string,
  counts: ReadonlyMap<string, number> | null = null,
): TreeSource[] {
  const list = registers?.registers ?? []
  const byShop = new Map<string, ReceiptRegisters['registers'][number][]>()
  for (const register of list) {
    const shop = register.shop === '' ? NO_SHOP : register.shop
    byShop.set(shop, [...(byShop.get(shop) ?? []), register])
  }
  const shiftsOf = (registerId: string): TreeSource[] => {
    if (registerId !== selectedRegisterId || shifts === undefined || shifts.registerId !== registerId) return []
    return [...shifts.shifts]
      .sort((left, right) => right.number - left.number)
      .slice(0, MAX_SHIFT_NODES)
      .map((shift) => ({
        id: shiftNode(registerId, shift.number),
        label: `Смена № ${shift.number}`,
        caption: [shift.isOpen === true ? 'открыта' : shift.isOpen === false ? 'закрыта' : null,
          shift.cleanup?.state === 'cleared' ? 'чеки в кабинете' : shift.cleanup?.state === 'problem' ? 'не очищается' : null]
          .filter((part) => part !== null).join(' · ') || null,
      }))
  }
  const of = (items: readonly ReceiptRegisters['registers'][number][]): number | null =>
    counts === null ? null : items.reduce((total, register) => total + (counts.get(register.serialNumber) ?? 0), 0)
  const shops: TreeSource[] = [...byShop.entries()].map(([shop, items]) => ({
    id: shopNode(shop),
    // «Без магазина» — не магазин, а группа касс без привязки: так и подписана.
    label: shop === NO_SHOP ? 'Кассы без магазина' : shop,
    caption: registersText(items.length),
    badge: countText(of(items)),
    children: items.map((register) => ({
      id: registerNode(register.id),
      label: register.title === '' ? `Касса ${register.serialNumber}` : register.title,
      badge: countText(of([register])),
      children: shiftsOf(register.id),
    })),
  }))
  return [{ id: ROOT_NODE, label: 'Весь кабинет', caption: registersText(list.length), badge: countText(of(list)), children: shops }]
}

export function treeNodesOf(
  registers: ReceiptRegisters | undefined,
  shifts: ReceiptShifts | undefined,
  filter: Pick<ReceiptsFilter, 'shop' | 'registerId' | 'shiftNumber'>,
  collapsed: readonly string[],
  counts: ReadonlyMap<string, number> | null = null,
): TreeNavNode[] {
  return flattenTree(treeSourceOf(registers, shifts, filter.registerId, counts), collapsed, selectedNodeOf(filter))
}

/** Выбранное словами: «Весь кабинет», «Магазин Один», «Магазин Один · AQSI 5 · SN-21», «… · смена № 6». */
export function selectionText(registers: ReceiptRegisters | undefined, filter: Pick<ReceiptsFilter, 'shop' | 'registerId' | 'shiftNumber'>): string {
  const register = filter.registerId === '' ? undefined : registers?.registers.find((item) => item.id === filter.registerId)
  const parts: string[] = []
  if (filter.registerId !== '') {
    if (register !== undefined) {
      if (register.shop !== '' && register.shop !== NO_SHOP) parts.push(register.shop)
      parts.push(register.title === '' ? `Касса ${register.serialNumber}` : register.title)
    } else {
      parts.push(`Касса ${filter.registerId}`)
    }
    if (filter.shiftNumber !== '') parts.push(`смена № ${filter.shiftNumber}`)
  } else if (filter.shop !== '') {
    parts.push(filter.shop)
  } else {
    parts.push('Весь кабинет')
  }
  return parts.join(' · ')
}
